import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PaginationBar from '../../components/PaginationBar';
import {
  ChecklistInputType,
  ChecklistType,
  InquiryChecklist,
  deleteInquiryChecklist,
  fetchInquiryChecklist,
  fetchInquiryChecklists,
} from '../../api/inquiryChecklists';
import SelectVehicleBrand from '../../components/SelectVehicleBrand';
import SelectVehicleModel from '../../components/SelectVehicleModel';

interface ChecklistsState {
  records: InquiryChecklist[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const DEFAULT_LIMIT = 20;

const renderInputTypeLabel = (type: ChecklistInputType) => {
  switch (type) {
    case 'boolean':
      return 'Yes / No';
    case 'text':
      return 'Free text';
    case 'single_select':
      return 'Single select';
    case 'multi_select':
      return 'Multi select';
    default:
      return type;
  }
};

const renderChecklistTypeLabel = (type: ChecklistType) => {
  switch (type) {
    case 'inspection':
      return 'Inspection';
    case 'pre_delivery':
      return 'Pre-delivery';
    default:
      return type;
  }
};

const VehicleInquiryChecklistsPanel: FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [state, setState] = useState<ChecklistsState>({
    records: [],
    total: 0,
    page: 1,
    limit: DEFAULT_LIMIT,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [selectedBrandId, setSelectedBrandId] = useState('');

  const [modelError, setModelError] = useState<string | null>(null);
  const [selectedModelId, setSelectedModelId] = useState('');
  const [selectedType, setSelectedType] = useState<ChecklistType | ''>('');
  const [mandatoryFilter, setMandatoryFilter] = useState<'all' | 'mandatory' | 'optional'>('all');

  useEffect(() => {
    const handle = window.setTimeout(
      () => setDebouncedSearch(search.trim()),
      350,
    );

    return () => window.clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setListError(null);

    fetchInquiryChecklists({
      page,
      limit: state.limit,
      search: debouncedSearch || undefined,
      brandId: selectedBrandId || undefined,
      modelId: selectedModelId || undefined,
      type: selectedType || undefined,
      isMandatory:
        mandatoryFilter === 'all'
          ? undefined
          : mandatoryFilter === 'mandatory'
            ? true
            : false,
    })
      .then((response) => {
        if (!isMounted) {
          return;
        }

        setState({
          records: response.data,
          total: response.meta.total,
          page: response.meta.page,
          limit: response.meta.limit,
          totalPages: response.meta.totalPages,
        });
      })
      .catch((err) => {
        if (!isMounted) {
          return;
        }
        const message = err instanceof Error ? err.message : 'Failed to load checklists.';
        setListError(message);
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [page, state.limit, debouncedSearch, refreshCounter, selectedBrandId, selectedModelId, selectedType, mandatoryFilter]);

  const handleModelChange = (modelId: string) => {
    setSelectedModelId(modelId);
    setPage(1);
  };

  const totalPages = useMemo(
    () => Math.max(1, state.totalPages || 1),
    [state.totalPages],
  );

  const summaryLabel = useMemo(() => {
    if (!state.total) {
      return 'No checklists found';
    }

    const first = (state.page - 1) * state.limit + 1;
    const last = first + state.records.length - 1;
    return `Showing ${first}-${last} of ${state.total} checklists`;
  }, [state.limit, state.page, state.records.length, state.total]);

  const handleDelete = async (checklist: InquiryChecklist) => {
    if (deletingId || !window.confirm(`Delete checklist "${checklist.name}"?`)) {
      return;
    }

    setDeletingId(checklist.id);
    setActionError(null);

    try {
      await deleteInquiryChecklist(checklist.id);
      if (state.records.length <= 1 && state.page > 1) {
        setPage((prev) => Math.max(1, prev - 1));
      }
      setRefreshCounter((prev) => prev + 1);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete checklist.';
      setActionError(message);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="rounded-2xl bg-white px-6 py-5 shadow-inner">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search checklists..."
            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-600 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 sm:w-80"
          />

          <button
            type="button"
            className="inline-flex items-center justify-center rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-primary/90"
            onClick={() => navigate('/vehicle-management/checklists/add')}
          >
            + Add
          </button>
        </div>



        <div className="flex flex-wrap items-center gap-3 sm:gap-4">
          <SelectVehicleBrand
            value={selectedBrandId}
            onChange={(brandId) => {
              setSelectedBrandId(brandId);
              setSelectedModelId('');
              setPage(1);
            }}
            label="Brand"
            labelClassName="text-xs font-semibold uppercase tracking-wide text-slate-400"
            className="flex w-full flex-col gap-1 sm:w-60"
            selectClassName="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
            helperClassName="text-[11px]"
            placeholderLabel="All brands"
          />

          <div className="flex w-full flex-col gap-1 sm:w-60">
            <SelectVehicleModel
              brandId={selectedBrandId || undefined}
              value={selectedModelId}
              onChange={handleModelChange}
              label="Model"
              labelClassName="text-xs font-semibold uppercase tracking-wide text-slate-400"
              selectClassName="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
              helperClassName="text-[11px]"
              placeholderLabel="All models"
              showStatus={false}
              onStatusChange={({ loading, error, models }) => {
                setModelError(error);
                if (!loading && !models.some((model) => model.id === selectedModelId)) {
                  setSelectedModelId('');
                }
              }}
            />
            {modelError ? (
              <span className="text-[11px] text-danger">{modelError}</span>
            ) : null}
          </div>

          <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
            Checklist type
            <select
              value={selectedType}
              onChange={(event) => {
                setSelectedType(event.target.value as ChecklistType | '');
                setPage(1);
              }}
              className="min-w-[12rem] rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value="">All types</option>
              <option value="inspection">Inspection</option>
              <option value="pre_delivery">Pre-delivery</option>
            </select>
          </label>

          <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
            Mandatory
            <select
              value={mandatoryFilter}
              onChange={(event) => {
                setMandatoryFilter(event.target.value as 'all' | 'mandatory' | 'optional');
                setPage(1);
              }}
              className="min-w-[10rem] rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value="all">All</option>
              <option value="mandatory">Mandatory</option>
              <option value="optional">Optional</option>
            </select>
          </label>
          {(selectedBrandId || selectedModelId || selectedType || mandatoryFilter !== 'all') && (
            <button
              type="button"
              className="mt-5 inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-primary/30 hover:text-primary shadow-sm"
              onClick={() => {
                setSearch('');
                setDebouncedSearch('');
                setSelectedBrandId('');
                setSelectedModelId('');
                setSelectedType('');
                setMandatoryFilter('all');
                setPage(1);
              }}
            >
              <span>✕</span> Clear Filter
            </button>
          )}
        </div>
      </div>

      {actionError ? (
        <p className="mt-3 text-sm text-danger">{actionError}</p>
      ) : null}

      <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200">
        <table className="min-w-full text-left text-sm text-slate-600">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-5 py-3 font-semibold">Name</th>
              <th className="px-5 py-3 font-semibold">Field type</th>
              <th className="px-5 py-3 font-semibold">Checklist type</th>
              <th className="px-5 py-3 font-semibold">Mandatory</th>
              <th className="px-5 py-3 font-semibold">Applies to</th>
              <th className="px-5 py-3 font-semibold">Updated</th>
              <th className="px-5 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="px-5 py-6 text-center text-slate-400" colSpan={7}>
                  Loading checklists...
                </td>
              </tr>
            ) : listError ? (
              <tr>
                <td className="px-5 py-6 text-center text-danger" colSpan={7}>
                  {listError}
                </td>
              </tr>
            ) : state.records.length === 0 ? (
              <tr>
                <td className="px-5 py-6 text-center text-slate-400" colSpan={7}>
                  No checklists found.
                </td>
              </tr>
            ) : (
              state.records.map((checklist) => {
                const brandNames = (checklist.brands || []).map((brand) => brand.name);
                const modelNames = (checklist.models || []).map((model) => model.name);
                const appliesTo =
                  brandNames.length === 0 && modelNames.length === 0
                    ? 'All brands & models'
                    : [
                      brandNames.length
                        ? `${brandNames.length} brand${brandNames.length > 1 ? 's' : ''}`
                        : null,
                      modelNames.length
                        ? `${modelNames.length} model${modelNames.length > 1 ? 's' : ''}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(', ');

                return (
                  <tr key={checklist.id} className="even:bg-slate-50/60">
                    <td className="px-5 py-3 font-medium text-slate-700">{checklist.name}</td>
                    <td className="px-5 py-3 text-slate-500">{renderInputTypeLabel(checklist.inputType)}</td>
                    <td className="px-5 py-3 text-slate-500">{renderChecklistTypeLabel(checklist.type)}</td>
                    <td className="px-5 py-3 text-slate-500">{checklist.isMandatory ? 'Yes' : 'No'}</td>
                    <td className="px-5 py-3 text-slate-500">{appliesTo}</td>
                    <td className="px-5 py-3 text-slate-500">
                      {new Date(checklist.updatedAt).toLocaleString()}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          type="button"
                          className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-primary hover:text-primary"
                          onClick={() => navigate(`/vehicle-management/checklists/${checklist.id}/view`)}
                        >
                          View
                        </button>
                        <button
                          type="button"
                          className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-primary hover:text-primary"
                          onClick={() => navigate(`/vehicle-management/checklists/${checklist.id}`)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="inline-flex items-center justify-center rounded-lg border border-primary/40 px-3 py-1.5 text-xs font-semibold text-primary transition hover:bg-primary/10"
                          onClick={async () => {
                            try {
                              const source = await fetchInquiryChecklist(checklist.id);
                              const query = new URLSearchParams({
                                duplicateId: source.id,
                                name: source.name,
                                description: source.description ?? '',
                                inputType: source.inputType,
                                type: source.type,
                                isMandatory: String(source.isMandatory),
                                options: JSON.stringify(source.answerOptions ?? []),
                                brandIds: JSON.stringify((source.brands ?? []).map((brand) => brand.id)),
                                modelIds: JSON.stringify((source.models ?? []).map((model) => model.id)),
                              }).toString();

                              navigate(`/vehicle-management/checklists/add?${query}`);
                            } catch (error) {
                              const message =
                                error instanceof Error
                                  ? error.message
                                  : 'Failed to duplicate checklist.';
                              setActionError(message);
                            }
                          }}
                        >
                          Clone
                        </button>
                        <button
                          type="button"
                          className="inline-flex items-center justify-center rounded-lg border border-danger/40 px-3 py-1.5 text-xs font-semibold text-danger transition hover:bg-danger/10 disabled:cursor-not-allowed disabled:opacity-60"
                          onClick={() => handleDelete(checklist)}
                          disabled={deletingId === checklist.id}
                        >
                          {deletingId === checklist.id ? 'Deleting…' : 'Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <PaginationBar
        page={state.page}
        totalPages={totalPages}
        onPrev={() => setPage((prev) => Math.max(1, prev - 1))}
        onNext={() => setPage((prev) => Math.min(totalPages, prev + 1))}
        onSelect={(num) => setPage(num)}
        disabled={loading}
        renderSummary={<span>{summaryLabel}</span>}
        renderExtra={
          <span className="text-xs text-slate-400">Page {state.page} of {totalPages} ({state.limit} / page)</span>
        }
      />
    </div>
  );
};

export default VehicleInquiryChecklistsPanel;
